import { Tooltip } from '@mui/material';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';
import { Dayjs } from 'dayjs';

import { errorNormalizer } from '../../../../shared/Helpers/functions';
import { parseWallClockTime } from '../../../../shared/Helpers/timezones';
import { SessionType as SessionTypeEnum } from '../../../../shared/Helpers/constants';
import { CustomButton } from '../../../../shared/uiComponents';
import { AdminTypes } from '../../../../components/Menu';
import ClockInDialog from '../../../../components/ClockInDialog';
import { useResetCalendarData } from '../../Helpers/hooks';

import { DispatchProperties, useSelector } from '../../../../redux/store';
import { startEventSession, startSession } from '../../../../redux/State/clientSlice/sessionSlice';
import { SoapNotesStatuses } from '../../../../redux/API/ClientAPIHelpers/dataCollectionProperties';
import { getEvents } from '../../../../redux/State/clientSlice/calendarSlice';
import { setInfoEvent, setInfoEventData } from '../../../../redux/State/clientSlice/calendarHelperSlice';
import { EventDataProperties, GetEventsProperties } from '../../../../redux/API/ClientAPIHelpers/calendarProperties';
import { SessionDataProperties } from '../../../../redux/API/ClientAPIHelpers/sessionsProperties';

const meetAndGreetTypes = [SessionTypeEnum.MeetAndGreet, SessionTypeEnum.MeetAndGreetNonBillable];
const pendingBTSessionDescription = 'You cannot edit session data in Pending BT status';

type CalendarFilterData = {
	client?: { id?: string } | null;
	includeBCBAEvents?: boolean;
	includeBTEvents?: boolean;
	therapist?: { id?: string } | null;
};

// Builds the payload used to turn a recurring-schedule ghost occurrence into a real session.
// The scheduled window (date/start/end) and the client + session type come from the ghost; the
// provider is the current user (resolved server-side from the auth token by start-session).
// Ghost start/end times are bare wall-clock strings (no offset), matching how the backend
// serializes real calendar events. Format to `YYYY-MM-DDTHH:mm` for the outbound API payload.
// When clockInStart/clockInEnd are provided, those are used as the actual session time.
export const buildRecurringStartSessionPayload = (data: EventDataProperties, clockInStart?: Dayjs | null, clockInEnd?: Dayjs | null): SessionDataProperties => {
	const dateStr = data.date.split('T')[0];
	const startTime = clockInStart
		? `${dateStr}T${clockInStart.format('HH:mm')}`
		: parseWallClockTime(data.startTime).format('YYYY-MM-DDTHH:mm');
	const endTime = clockInEnd
		? `${dateStr}T${clockInEnd.format('HH:mm')}`
		: parseWallClockTime(data.endTime).format('YYYY-MM-DDTHH:mm');

	return {
		clientId: data.client.id,
		date: data.date,
		endTime,
		recurringScheduleId: data.recurringScheduleId,
		recurringScheduleTimeSlotId: data.recurringScheduleTimeSlotId,
		sessionTypeId: data.sessionType.id,
		startTime,
	};
};

export const getStartSessionRefreshEventData = ({
	canFilterEvents,
	days,
	filterData,
}: {
	canFilterEvents: boolean;
	days: GetEventsProperties['days'];
	filterData: CalendarFilterData;
}): GetEventsProperties => {
	const includeBTEvents = filterData.includeBTEvents;
	const includeBCBAEvents = filterData.includeBCBAEvents;

	if (!canFilterEvents) return { days, includeBTEvents, includeBCBAEvents };

	return {
		days,
		clientId: filterData.client?.id,
		userId: filterData.therapist?.id,
		includeBTEvents,
		includeBCBAEvents,
	};
};

const SessionActions = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const navigate = useNavigate();
	const { resetData } = useResetCalendarData();

	const [loading, setLoading] = useState<boolean>(false);
	const [clockInDialogOpen, setClockInDialogOpen] = useState<boolean>(false);

	const role = useSelector((state) => state.account.role);
	const days = useSelector((state) => state.calendarHelper.displayedCalendarPageDays);
	const data = useSelector((state) => state.calendarHelper.infoEventData);

	const isAdmin = role?.section.id === AdminTypes.admin;
	const canFilterEvents = role?.section.id === AdminTypes.admin || role?.section.id === AdminTypes.cs;
	const isBT = role?.section.id === AdminTypes.bt;
	const expired =
		SoapNotesStatuses.expired48 === data?.reportStatus?.status ||
		SoapNotesStatuses.expiredWeek === data?.reportStatus?.status;
	const filterData = useSelector((state) => state.calendarHelper.filterData);

	const canSkipSession = (data && meetAndGreetTypes.includes(data.sessionType.type)) || !isBT;


	const collectDataHandler = () => {
		if (!data) return;
		const { sessionId, client } = data;
		resetData();
		navigate(`/calendar/session/${sessionId}/${client.id}`);
	};

	const handleStartSessionClick = () => {
		if (!data?.id) return;
		if (data.isRecurringScheduleSession) {
			setClockInDialogOpen(true);
		} else {
			executeStartSession();
		}
	};

	const executeStartSession = (clockInStart?: Dayjs | null, clockInEnd?: Dayjs | null) => {
		if (!data?.id) return;
		setLoading(true);

		const request = data.isRecurringScheduleSession
			? dispatch(startSession(buildRecurringStartSessionPayload(data, clockInStart, clockInEnd))).then(unwrapResult)
			: dispatch(startEventSession(data.id)).then(unwrapResult);

		request
			.then((response) => {
				if (!response.id || !response.client.id || !days.length) return;
				dispatch(setInfoEventData(response));
				dispatch(getEvents(getStartSessionRefreshEventData({ canFilterEvents, days, filterData })));
				setClockInDialogOpen(false);

				if (!canSkipSession) {
					dispatch(setInfoEvent(false));
					navigate(`/calendar/session/${response.id}/${response.client.id}`);
				}
			})
			.catch(errorNormalizer)
			.finally(() => setLoading(false));
	};

	if (data?.isCancelled) return null;

	return (
		<>
			{data && !data.isCompleted ? (
				data.sessionId ? (
					<CustomButton
						title="Collect Data"
						onClick={collectDataHandler}
						className="flex100"
						disabled={expired && !isAdmin}
					/>
				) : (
					<CustomButton
						title={canSkipSession ? 'Collect Data' : 'Start Session'}
						onClick={handleStartSessionClick}
						loading={loading}
						className="flex100"
						disabled={expired && !isAdmin}
					/>
				)
			) : (
				<Tooltip
					disableHoverListener={SoapNotesStatuses.pendingBT !== data?.reportStatus?.status || isAdmin}
					title={pendingBTSessionDescription}
				>
					<span className="flex100">
						<CustomButton title="Session Ended" disabled={!isAdmin} onClick={collectDataHandler} />
					</span>
				</Tooltip>
			)}
			{data && (
				<ClockInDialog
					open={clockInDialogOpen}
					onClose={() => {
						setClockInDialogOpen(false);
						dispatch(setInfoEvent(false));
					}}
					onConfirm={(start, end) => executeStartSession(start, end)}
					loading={loading}
					scheduledStartTime={data.startTime}
					scheduledEndTime={data.endTime}
				/>
			)}
		</>
	);
};

export default SessionActions;
